import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

const pluginKey = new PluginKey("nodeSelectionDrag");

/**
 * Find the cursor position at the end of the last text block in the
 * last row. Returns null if the last row has no text blocks.
 */
export function findLastTextBlockEnd(
  doc: import("@tiptap/pm/model").Node,
): number | null {
  if (doc.childCount === 0) return null;

  // Find the last row position
  let lastRowPos = 0;
  doc.forEach((_, offset) => {
    lastRowPos = offset;
  });
  const lastRow = doc.nodeAt(lastRowPos);
  if (!lastRow) return null;

  const lastCell = lastRow.lastChild;
  if (!lastCell) return null;

  // Calculate the start of the last cell's content
  let cellContentStart = lastRowPos + 1; // inside row
  for (let c = 0; c < lastRow.childCount - 1; c++) {
    cellContentStart += lastRow.child(c).nodeSize;
  }
  cellContentStart += 1; // inside cell

  // Find the last text block in the cell
  let result: number | null = null;
  let pos = cellContentStart;
  for (let i = 0; i < lastCell.childCount; i++) {
    const block = lastCell.child(i);
    if (block.isTextblock) {
      result = pos + 1 + block.content.size;
    }
    pos += block.nodeSize;
  }
  return result;
}

/** In-memory clipboard for row-level copy/paste. */
let copiedRowJSON: Record<string, unknown> | null = null;

/**
 * Tracks which cell (column) the user last clicked when creating
 * a row-level NodeSelection. Bubble menus use this to target the
 * correct cell in multi-column rows.
 */
let _clickedCellIndex = 0;

/** Get the cell index that was last clicked in a row NodeSelection. */
export function getClickedCellIndex(): number {
  return _clickedCellIndex;
}

/**
 * Tracks which specific non-text block type (e.g. "emailImage", "emailButton")
 * was clicked. When a cell contains multiple non-text blocks, bubble menus use
 * this to only show for the block that was actually clicked.
 */
let _clickedNonTextBlockType: string | null = null;

/** Get the non-text block type that was last clicked. */
export function getClickedNonTextBlockType(): string | null {
  return _clickedNonTextBlockType;
}

/**
 * Tracks which nth block of the same type was clicked within the cell.
 * E.g., if a cell has 2 emailImage blocks, clicking the second sets this to 1.
 */
let _clickedNonTextBlockIndex = 0;

/** Get the index of the clicked non-text block among same-type siblings in the cell. */
export function getClickedNonTextBlockIndex(): number {
  return _clickedNonTextBlockIndex;
}

/**
 * Count how many same-type blocks precede the block at `blockPos` within
 * the cell at `cellIndex` of the row at `rowPos`.
 */
function countSameTypeBlocksBefore(
  doc: import("@tiptap/pm/model").Node,
  rowPos: number,
  cellIndex: number,
  blockType: string,
  blockPos: number,
): number {
  const rowNode = doc.nodeAt(rowPos);
  if (!rowNode || cellIndex >= rowNode.childCount) return 0;

  let cellStart = rowPos + 1;
  for (let c = 0; c < cellIndex; c++) cellStart += rowNode.child(c).nodeSize;
  const cell = rowNode.child(cellIndex);

  let offset = cellStart + 1;
  let idx = 0;
  for (let i = 0; i < cell.childCount; i++) {
    const block = cell.child(i);
    if (block.type.name === blockType) {
      if (offset === blockPos) return idx;
      idx++;
    }
    offset += block.nodeSize;
  }
  return 0;
}

/**
 * Resolve a click position to a non-text block and update the module-level
 * tracking variables (_clickedNonTextBlockType, _clickedNonTextBlockIndex).
 * Returns the block type if found, null otherwise.
 */
function updateBlockTracking(
  view: EditorView,
  clickPos: number,
  rowPos: number,
  cellIndex: number,
): string | null {
  const $pos = view.state.doc.resolve(clickPos);
  let blockType: string | null = null;
  let blockPos: number | null = null;

  for (let d = $pos.depth; d >= 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === "emailImage" || name === "emailButton") {
      blockType = name;
      blockPos = $pos.before(d);
      break;
    }
  }
  if (!blockType) {
    const nodeAfter = $pos.nodeAfter;
    if (
      nodeAfter &&
      (nodeAfter.type.name === "emailImage" ||
        nodeAfter.type.name === "emailButton")
    ) {
      blockType = nodeAfter.type.name;
      blockPos = clickPos;
    }
  }
  if (!blockType) {
    const nodeBefore = $pos.nodeBefore;
    if (
      nodeBefore &&
      (nodeBefore.type.name === "emailImage" ||
        nodeBefore.type.name === "emailButton")
    ) {
      blockType = nodeBefore.type.name;
      blockPos = clickPos - nodeBefore.nodeSize;
    }
  }

  if (blockType && blockPos !== null) {
    _clickedNonTextBlockType = blockType;
    _clickedNonTextBlockIndex = countSameTypeBlocksBefore(
      view.state.doc,
      rowPos,
      cellIndex,
      blockType,
      blockPos,
    );
  } else {
    _clickedNonTextBlockType = null;
    _clickedNonTextBlockIndex = 0;
  }
  return blockType;
}

interface DragState {
  sourcePos: number;
  sourceSize: number;
  active: boolean;
}

/** Find all top-level node positions + their DOM rects. */
function getRowRects(view: EditorView) {
  const { doc } = view.state;
  const rows: { pos: number; size: number; rect: DOMRect }[] = [];
  doc.forEach((node, offset) => {
    const dom = view.nodeDOM(offset);
    if (dom instanceof HTMLElement) {
      rows.push({
        pos: offset,
        size: node.nodeSize,
        rect: dom.getBoundingClientRect(),
      });
    }
  });
  return rows;
}

/** From a Y coordinate, find the insertion index (0 = before first row). */
function getDropIndex(rows: { rect: DOMRect }[], clientY: number) {
  for (let i = 0; i < rows.length; i++) {
    const mid = rows[i].rect.top + rows[i].rect.height / 2;
    if (clientY < mid) return i;
  }
  return rows.length;
}

/** Execute the row move via a ProseMirror transaction. */
function moveRow(view: EditorView, sourcePos: number, clientY: number) {
  const { doc } = view.state;
  const rows: { pos: number; size: number }[] = [];
  doc.forEach((node, offset) => {
    rows.push({ pos: offset, size: node.nodeSize });
  });

  const sourceIdx = rows.findIndex((r) => r.pos === sourcePos);
  if (sourceIdx === -1) return;

  const domRows = getRowRects(view);
  const targetIdx = getDropIndex(domRows, clientY);

  if (targetIdx === sourceIdx || targetIdx === sourceIdx + 1) return;

  const sourceRow = rows[sourceIdx];
  const sourceNode = doc.nodeAt(sourceRow.pos);
  if (!sourceNode) return;

  const { tr } = view.state;
  tr.delete(sourceRow.pos, sourceRow.pos + sourceRow.size);

  let insertPos: number;
  if (targetIdx > sourceIdx) {
    const adjustedTarget = targetIdx - 1;
    if (adjustedTarget >= rows.length - 1) {
      insertPos = tr.doc.content.size;
    } else {
      insertPos = rows[adjustedTarget + 1].pos - sourceRow.size;
    }
  } else {
    insertPos = rows[targetIdx].pos;
  }

  tr.insert(insertPos, sourceNode);
  tr.setSelection(NodeSelection.create(tr.doc, insertPos));
  view.dispatch(tr);
}

/**
 * Find the emailRow for a click event using multiple strategies:
 *
 * 1. Walk up event.target DOM to find a row via view.posAtDOM
 *    (works for clicks on content inside blocks)
 * 2. Use posAtCoords to resolve position and walk up the doc tree
 *    (works for clicks on text content)
 * 3. Find the row whose .email-row DOM element's Y-range contains the click
 *    (works for clicks on cell padding, table gaps, flex gaps)
 *
 * Strategy 3 uses the .email-row child elements (cells) to compute a proper
 * bounding rect since display:table rows can have tiny rects.
 */
function findRowFromEvent(
  view: EditorView,
  event: MouseEvent,
): { rowPos: number; rowSize: number } | null {
  // Strategy 1: Walk up from event.target
  let target = event.target as HTMLElement | null;
  while (target && target !== view.dom) {
    try {
      const pos = view.posAtDOM(target, 0);
      const $pos = view.state.doc.resolve(pos);
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type.name === "emailRow") {
          const rowPos = $pos.before(d);
          const rowNode = view.state.doc.nodeAt(rowPos);
          if (rowNode) return { rowPos, rowSize: rowNode.nodeSize };
        }
      }
    } catch {
      // posAtDOM can throw for some DOM nodes; keep walking up
    }
    target = target.parentElement;
  }

  // Strategy 2: posAtCoords + doc tree walk.
  // Verify the click is within the row's visual bounds to avoid
  // posAtCoords resolving padding clicks to the nearest text position.
  const coords = view.posAtCoords({
    left: event.clientX,
    top: event.clientY,
  });
  if (coords) {
    const $pos = view.state.doc.resolve(coords.pos);
    for (let d = $pos.depth; d >= 0; d--) {
      if ($pos.node(d).type.name === "emailRow") {
        const rowPos = $pos.before(d);
        const rowNode = view.state.doc.nodeAt(rowPos);
        if (rowNode) {
          // Verify click Y is within the row's visual bounds
          const rowDOM = view.nodeDOM(rowPos);
          if (rowDOM instanceof HTMLElement) {
            let rect = rowDOM.getBoundingClientRect();
            if (rect.height < 5 && rowDOM.firstElementChild) {
              rect = rowDOM.firstElementChild.getBoundingClientRect();
            }
            if (event.clientY >= rect.top && event.clientY <= rect.bottom) {
              return { rowPos, rowSize: rowNode.nodeSize };
            }
          } else {
            return { rowPos, rowSize: rowNode.nodeSize };
          }
        }
      }
    }
  }

  // Strategy 3: Find row whose visual Y-range contains the click.
  // Use the row's first child element for the bounding rect since
  // display:table elements can have collapsed rects.
  const { doc } = view.state;
  const clientY = event.clientY;
  let result: { rowPos: number; rowSize: number } | null = null;
  doc.forEach((node, offset) => {
    if (result) return;
    const dom = view.nodeDOM(offset);
    if (!(dom instanceof HTMLElement)) return;

    // Get a reliable rect: try the element itself, then fall back to
    // the first child (which may have a better rect for table layouts)
    let rect = dom.getBoundingClientRect();
    if (rect.height < 5 && dom.firstElementChild) {
      rect = dom.firstElementChild.getBoundingClientRect();
    }

    if (clientY >= rect.top && clientY <= rect.bottom) {
      result = { rowPos: offset, rowSize: node.nodeSize };
    }
  });

  return result;
}

/**
 * Determine which cell (column index) a click landed in for a given row.
 * Uses posAtCoords to resolve the click position, then walks up the doc
 * tree to find the emailCell and counts its index within the row.
 */
function findCellIndexFromEvent(
  view: EditorView,
  event: MouseEvent,
  rowPos: number,
): number {
  const coords = view.posAtCoords({
    left: event.clientX,
    top: event.clientY,
  });
  if (!coords) return 0;

  try {
    const $pos = view.state.doc.resolve(coords.pos);
    for (let d = $pos.depth; d >= 0; d--) {
      if ($pos.node(d).type.name === "emailCell") {
        // Found the cell — count its index within the row
        const cellPos = $pos.before(d);
        const rowNode = view.state.doc.nodeAt(rowPos);
        if (!rowNode) return 0;
        let offset = rowPos + 1; // inside the row
        for (let i = 0; i < rowNode.childCount; i++) {
          if (offset === cellPos) return i;
          offset += rowNode.child(i).nodeSize;
        }
      }
    }
  } catch {
    // resolve can fail for edge positions
  }
  return 0;
}

/**
 * Check if a position is inside a non-text block (image) that
 * should not allow text editing on double-click.
 */
function isInsideNonTextBlock(view: EditorView, pos: number): boolean {
  const $pos = view.state.doc.resolve(pos);
  // Check ancestors (for content nodes like emailImage)
  for (let d = $pos.depth; d >= 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === "emailImage" || name === "emailButton") return true;
  }
  // Check the node at/after/before this position (for leaf/atom nodes)
  const nodeAfter = $pos.nodeAfter;
  if (nodeAfter) {
    const name = nodeAfter.type.name;
    if (name === "emailImage" || name === "emailButton") return true;
  }
  const nodeBefore = $pos.nodeBefore;
  if (nodeBefore) {
    const name = nodeBefore.type.name;
    if (name === "emailImage" || name === "emailButton") return true;
  }
  return false;
}

/**
 * Return the name of the non-text block at `pos`, or null if the position
 * is not inside a non-text block.
 */
function getNonTextBlockTypeAt(view: EditorView, pos: number): string | null {
  const $pos = view.state.doc.resolve(pos);
  for (let d = $pos.depth; d >= 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === "emailImage" || name === "emailButton") return name;
  }
  const nodeAfter = $pos.nodeAfter;
  if (nodeAfter) {
    const name = nodeAfter.type.name;
    if (name === "emailImage" || name === "emailButton") return name;
  }
  const nodeBefore = $pos.nodeBefore;
  if (nodeBefore) {
    const name = nodeBefore.type.name;
    if (name === "emailImage" || name === "emailButton") return name;
  }
  return null;
}

function isWordChar(ch: string): boolean {
  // Case-folding catches accented and non-Latin letters; \p{L} needs the u
  // flag, which this package's es5 target rejects.
  return /\w/.test(ch) || ch.toLowerCase() !== ch.toUpperCase();
}

function wordRangeAt(
  view: EditorView,
  pos: number,
): { from: number; to: number } | null {
  const $pos = view.state.doc.resolve(pos);
  const parent = $pos.parent;
  if (!parent.isTextblock) return null;

  // One placeholder char per inline leaf, so offsets stay content positions.
  const text = parent.textBetween(0, parent.content.size, undefined, "￼");
  const offset = $pos.parentOffset;
  let start = offset;
  let end = offset;
  while (start > 0 && isWordChar(text[start - 1])) start--;
  while (end < text.length && isWordChar(text[end])) end++;
  if (start === end) return null;

  const base = $pos.start();
  return { from: base + start, to: base + end };
}

const NON_TEXT_BLOCKS = new Set(["emailImage", "emailButton"]);

/** Check if the current selection is a NodeSelection on a non-text block or its row. */
function isNonTextNodeSelected(view: EditorView): boolean {
  const { selection } = view.state;
  if (!(selection instanceof NodeSelection)) return false;
  const node = selection.node;

  // Direct NodeSelection on the atom (e.g. ArrowLeft into a button)
  if (NON_TEXT_BLOCKS.has(node.type.name)) return true;

  // NodeSelection on a row — check the clicked cell for a non-text block
  if (node.type.name === "emailRow") {
    const clickedIdx = getClickedCellIndex();
    const cellIdx = clickedIdx < node.childCount ? clickedIdx : 0;
    const cell = node.child(cellIdx);
    const block = cell?.firstChild;
    if (
      block &&
      cell.childCount === 1 &&
      NON_TEXT_BLOCKS.has(block.type.name)
    ) {
      return true;
    }
    // Fallback: check first cell (single-cell rows)
    const firstCell = node.firstChild;
    const firstBlock = firstCell?.firstChild;
    return (
      !!firstBlock &&
      firstCell?.childCount === 1 &&
      NON_TEXT_BLOCKS.has(firstBlock.type.name)
    );
  }

  return false;
}

/**
 * ProseMirror plugin that implements:
 * 1. Single click → NodeSelection on the parent emailRow (block selection)
 * 2. Double click → TextSelection at click position (enter editing)
 * 3. Drag reordering when a row is NodeSelected
 *
 * All click handling is done in mousedown to prevent ProseMirror from
 * setting TextSelection before we can intervene.
 */
export function createNodeSelectionDragPlugin() {
  let dragState: DragState | null = null;
  let dropIndicator: HTMLDivElement | null = null;
  let dragHighlight: HTMLDivElement | null = null;
  let lastClickTime = 0;
  let lastClickCoords: { x: number; y: number } | null = null;
  let pendingDeselect: { rowPos: number; time: number } | null = null;
  let deselectTimer: ReturnType<typeof setTimeout> | null = null;

  function showDropIndicator(view: EditorView, clientY: number) {
    const rows = getRowRects(view);
    if (rows.length === 0) return;
    const idx = getDropIndex(rows, clientY);

    let indicatorY: number;
    if (idx === 0) {
      indicatorY = rows[0].rect.top;
    } else if (idx >= rows.length) {
      indicatorY = rows[rows.length - 1].rect.bottom;
    } else {
      indicatorY = (rows[idx - 1].rect.bottom + rows[idx].rect.top) / 2;
    }

    if (!dropIndicator) {
      dropIndicator = document.createElement("div");
      dropIndicator.style.cssText =
        "position:fixed;left:0;right:0;height:2px;background:rgb(var(--link));pointer-events:none;z-index:9999;transition:top 60ms ease-out;";
      document.body.appendChild(dropIndicator);
    }

    if (rows.length > 0) {
      const rowRect = rows[0].rect;
      dropIndicator.style.left = `${rowRect.left}px`;
      dropIndicator.style.right = `${document.documentElement.clientWidth - rowRect.right}px`;
    }
    dropIndicator.style.top = `${indicatorY}px`;
  }

  function showDragHighlight(view: EditorView, pos: number) {
    const dom = view.nodeDOM(pos);
    if (!(dom instanceof HTMLElement)) return;
    const rect = dom.getBoundingClientRect();

    if (!dragHighlight) {
      dragHighlight = document.createElement("div");
      dragHighlight.style.cssText =
        "position:fixed;pointer-events:none;z-index:50;border-radius:4px;background:rgb(var(--link) / 8%);";
      document.body.appendChild(dragHighlight);
    }

    dragHighlight.style.top = `${rect.top}px`;
    dragHighlight.style.left = `${rect.left}px`;
    dragHighlight.style.width = `${rect.width}px`;
    dragHighlight.style.height = `${rect.height}px`;
  }

  function cleanup() {
    dropIndicator?.remove();
    dropIndicator = null;
    dragHighlight?.remove();
    dragHighlight = null;
    dragState = null;
    document.body.style.userSelect = "";
  }

  function startDrag(
    view: EditorView,
    event: MouseEvent,
    rowPos: number,
    rowSize: number,
  ) {
    dragState = { sourcePos: rowPos, sourceSize: rowSize, active: false };
    const startY = event.clientY;

    const onMouseMove = (e: MouseEvent) => {
      if (!dragState) return;
      if (!dragState.active && Math.abs(e.clientY - startY) < 5) return;
      if (!dragState.active) {
        dragState.active = true;
        document.body.style.userSelect = "none";
        showDragHighlight(view, dragState.sourcePos);
      }
      e.preventDefault();
      showDropIndicator(view, e.clientY);
    };

    const onMouseUp = (e: MouseEvent) => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keydown", onEscKey);
      if (dragState?.active) {
        moveRow(view, dragState.sourcePos, e.clientY);
      } else {
        // Click-without-drag on already-selected row: keep NodeSelection.
        // Don't deselect here — this preserves NodeSelection so a rapid
        // second click (double-click) can detect isCurrentlyNodeSelected
        // and enter text editing mode. Deselection on single-click is
        // handled separately via a delayed check.
        // Schedule deselect after double-click window (400ms).
        // If a second click arrives, mousedown cancels this timer.
        const deselectView = view;
        if (deselectTimer) clearTimeout(deselectTimer);
        deselectTimer = setTimeout(() => {
          pendingDeselect = null;
          deselectTimer = null;
          // Only deselect if still NodeSelected on the same row
          const sel = deselectView.state.selection;
          if (
            sel instanceof NodeSelection &&
            sel.node.type.name === "emailRow" &&
            sel.from === dragState?.sourcePos
          ) {
            // Deselect won't work here since dragState is cleaned up;
            // use the captured rowPos instead.
          }
          const tr = deselectView.state.tr.setSelection(
            TextSelection.near(deselectView.state.doc.resolve(0), 1),
          );
          deselectView.dispatch(tr);
          (deselectView.dom as HTMLElement).blur();
        }, 400);
      }
      cleanup();
    };

    const onEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.removeEventListener("keydown", onEscKey);
        cleanup();
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keydown", onEscKey);
  }

  return new Plugin({
    key: pluginKey,
    view(editorView) {
      // Capture-phase dragstart listener on the editor DOM.
      // This fires BEFORE ProseMirror's own event handlers and prevents
      // native node dragging when the user is in text editing mode.
      const handler = (e: Event) => {
        if (editorView.state.selection instanceof TextSelection) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      editorView.dom.addEventListener("dragstart", handler, true);
      return {
        destroy() {
          editorView.dom.removeEventListener("dragstart", handler, true);
        },
      };
    },
    props: {
      // Prevent ProseMirror's default click handler from converting
      // NodeSelection to TextSelection after our mousedown handler.
      handleClick(view) {
        if (view.state.selection instanceof NodeSelection) return true;
        return false;
      },
      handleDoubleClick() {
        // Suppress ProseMirror's native double-click handling
        return true;
      },
      handleDOMEvents: {
        // Block all input at the DOM level when a non-text node (button,
        // image) is NodeSelected. Also handle arrow key navigation to
        // move between rows.
        keydown(view, event) {
          const { selection } = view.state;
          const isMod = event.metaKey || event.ctrlKey;

          // ── Row-level copy/cut/paste/duplicate ──
          // Works from any selection state: NodeSelection copies the
          // selected row; paste inserts after the current row.

          // Copy / Cut / Duplicate: require NodeSelection on a row
          if (selection instanceof NodeSelection && isMod) {
            const selNode = selection.node;
            let rowNode = selNode;
            let rowStart = selection.from;
            if (selNode.type.name !== "emailRow") {
              const $sel = view.state.doc.resolve(selection.from);
              for (let d = $sel.depth; d >= 0; d--) {
                if ($sel.node(d).type.name === "emailRow") {
                  rowNode = $sel.node(d);
                  rowStart = $sel.before(d);
                  break;
                }
              }
            }

            if (rowNode.type.name === "emailRow") {
              // Cmd+C → copy
              if (event.key === "c") {
                copiedRowJSON = rowNode.toJSON() as Record<string, unknown>;
                return false; // let browser handle text clipboard too
              }

              // Cmd+X → cut
              if (event.key === "x") {
                copiedRowJSON = rowNode.toJSON() as Record<string, unknown>;
                event.preventDefault();
                const { tr } = view.state;
                tr.delete(rowStart, rowStart + rowNode.nodeSize);
                if (tr.doc.content.size === 0) {
                  const schema = view.state.schema;
                  const newRow = schema.nodes["emailRow"].create(null, [
                    schema.nodes["emailCell"].create({ colSpan: 4 }, [
                      schema.nodes["emailParagraph"].create(),
                    ]),
                  ]);
                  tr.insert(0, newRow);
                  tr.setSelection(TextSelection.near(tr.doc.resolve(3)));
                } else {
                  tr.setSelection(
                    TextSelection.near(
                      tr.doc.resolve(
                        Math.min(rowStart, tr.doc.content.size - 1),
                      ),
                      1,
                    ),
                  );
                }
                view.dispatch(tr);
                return true;
              }

              // Cmd+D → duplicate
              if (event.key === "d") {
                event.preventDefault();
                const { tr } = view.state;
                const insertPos = rowStart + rowNode.nodeSize;
                const copy = view.state.schema.nodeFromJSON(
                  rowNode.toJSON() as Record<string, unknown>,
                );
                tr.insert(insertPos, copy);
                tr.setSelection(NodeSelection.create(tr.doc, insertPos));
                view.dispatch(tr);
                return true;
              }
            }
          }

          // Paste: only when a row is NodeSelected (not during text editing).
          // Inserts the copied row after the selected row.
          if (
            selection instanceof NodeSelection &&
            isMod &&
            event.key === "v" &&
            copiedRowJSON
          ) {
            let rowNode = selection.node;
            let rowStart = selection.from;
            if (rowNode.type.name !== "emailRow") {
              const $sel = view.state.doc.resolve(selection.from);
              for (let d = $sel.depth; d >= 0; d--) {
                if ($sel.node(d).type.name === "emailRow") {
                  rowNode = $sel.node(d);
                  rowStart = $sel.before(d);
                  break;
                }
              }
            }
            if (rowNode.type.name === "emailRow") {
              event.preventDefault();
              try {
                const newNode = view.state.schema.nodeFromJSON(copiedRowJSON);
                const { tr } = view.state;
                const insertPos = rowStart + rowNode.nodeSize;
                tr.insert(insertPos, newNode);
                tr.setSelection(NodeSelection.create(tr.doc, insertPos));
                view.dispatch(tr);
              } catch {
                // Invalid JSON, ignore
              }
              return true;
            }
          }

          if (!isNonTextNodeSelected(view)) return false;

          const sel = selection as NodeSelection;

          // Resolve to the parent emailRow regardless of whether the
          // NodeSelection is on the row itself or on a child atom.
          let rowPos: number;
          let rowSize: number;
          if (sel.node.type.name === "emailRow") {
            rowPos = sel.from;
            rowSize = sel.node.nodeSize;
          } else {
            // Selection is on a child atom — walk up to find the row
            const $sel = view.state.doc.resolve(sel.from);
            let found = false;
            for (let d = $sel.depth; d >= 0; d--) {
              if ($sel.node(d).type.name === "emailRow") {
                rowPos = $sel.before(d);
                rowSize = $sel.node(d).nodeSize;
                found = true;
                break;
              }
            }
            if (!found) return false;
            rowPos = rowPos!;
            rowSize = rowSize!;
          }

          // Arrow Down / Right → move to next row or create one
          if (event.key === "ArrowDown" || event.key === "ArrowRight") {
            event.preventDefault();
            const afterRow = rowPos + rowSize;
            if (afterRow < view.state.doc.content.size) {
              // Move to next row
              const tr = view.state.tr.setSelection(
                NodeSelection.create(view.state.doc, afterRow),
              );
              view.dispatch(tr);
            } else {
              // Last row: create a new empty row below
              const { tr } = view.state;
              const schema = view.state.schema;
              const rowType = schema.nodes["emailRow"];
              const cellType = schema.nodes["emailCell"];
              const paragraphType = schema.nodes["emailParagraph"];
              if (rowType && cellType && paragraphType) {
                const newRow = rowType.create(null, [
                  cellType.create({ colSpan: 4 }, [paragraphType.create()]),
                ]);
                tr.insert(afterRow, newRow);
                tr.setSelection(
                  TextSelection.near(tr.doc.resolve(afterRow + 3)),
                );
                view.dispatch(tr);
                view.focus();
              }
            }
            return true;
          }

          // Arrow Up / Left → move to previous row
          if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
            event.preventDefault();
            if (rowPos > 0) {
              const $before = view.state.doc.resolve(rowPos);
              const prevRowPos = $before.before($before.depth);
              if (prevRowPos >= 0 && prevRowPos < rowPos) {
                const tr = view.state.tr.setSelection(
                  NodeSelection.create(view.state.doc, prevRowPos),
                );
                view.dispatch(tr);
              }
            }
            return true;
          }

          // Escape → deselect
          if (event.key === "Escape") {
            event.preventDefault();
            const tr = view.state.tr.setSelection(
              TextSelection.near(view.state.doc.resolve(0), 1),
            );
            view.dispatch(tr);
            (view.dom as HTMLElement).blur();
            return true;
          }

          // Backspace / Delete → delete the row containing the non-text block
          if (event.key === "Backspace" || event.key === "Delete") {
            event.preventDefault();
            const { tr } = view.state;
            tr.delete(rowPos, rowPos + rowSize);
            if (tr.doc.content.size === 0) {
              const schema = view.state.schema;
              const newRow = schema.nodes["emailRow"].create(null, [
                schema.nodes["emailCell"].create({ colSpan: 4 }, [
                  schema.nodes["emailParagraph"].create(),
                ]),
              ]);
              tr.insert(0, newRow);
              tr.setSelection(TextSelection.near(tr.doc.resolve(3)));
            } else {
              const newPos = Math.min(rowPos, tr.doc.content.size - 1);
              tr.setSelection(
                NodeSelection.create(tr.doc, Math.max(0, newPos)),
              );
            }
            view.dispatch(tr);
            return true;
          }

          // Block printable character input (no typing on image/button)
          if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            return true;
          }

          return false;
        },
        // Block text insertion (beforeinput) when a non-text node is selected,
        // but allow paste so ProseMirror can handle it as a node paste.
        beforeinput(view, event) {
          if (!isNonTextNodeSelected(view)) return false;
          const inputType = (event as InputEvent).inputType;
          if (inputType === "insertFromPaste") return false;
          event.preventDefault();
          return true;
        },
        mousedown(view, event) {
          if (event.button !== 0) return false;

          // Let cell resize handles pass through to the cell resize plugin
          const clickTarget = event.target as HTMLElement;
          if (
            clickTarget.classList.contains("email-cell-resize-handle") ||
            clickTarget.classList.contains("email-cell-resize-handle-left")
          ) {
            return false;
          }

          // Cancel any pending deselect from a previous click-without-drag.
          // This allows double-click to work: first click keeps NodeSelection
          // (schedules deselect), second click cancels that deselect and
          // enters text editing.
          if (deselectTimer) {
            clearTimeout(deselectTimer);
            deselectTimer = null;
            pendingDeselect = null;
          }

          const now = Date.now();
          const isDoubleClick =
            lastClickCoords &&
            now - lastClickTime < 400 &&
            Math.abs(event.clientX - lastClickCoords.x) < 5 &&
            Math.abs(event.clientY - lastClickCoords.y) < 5;

          // Find which row the click landed in by walking up the DOM
          // from event.target. This is reliable regardless of CSS
          // table-row layout or posAtCoords resolving to doc boundary.
          const rowHit = findRowFromEvent(view, event);
          if (!rowHit) {
            // Update click tracking so double-click detection works
            // even when the first click was outside all rows.
            lastClickTime = now;
            lastClickCoords = { x: event.clientX, y: event.clientY };

            // Double-click below all rows → create a new empty row
            if (isDoubleClick) {
              const schema = view.state.schema;
              const rowType = schema.nodes["emailRow"];
              const cellType = schema.nodes["emailCell"];
              const paragraphType = schema.nodes["emailParagraph"];
              if (rowType && cellType && paragraphType) {
                event.preventDefault();
                const { tr } = view.state;
                const endPos = view.state.doc.content.size;
                const newRow = rowType.create(null, [
                  cellType.create({ colSpan: 4 }, [paragraphType.create()]),
                ]);
                tr.insert(endPos, newRow);
                tr.setSelection(TextSelection.near(tr.doc.resolve(endPos + 3)));
                view.dispatch(tr);
                view.focus();
                lastClickTime = 0;
                lastClickCoords = null;
                return true;
              }
            }

            // Single click below all rows: focus end of last text block
            const textEnd = findLastTextBlockEnd(view.state.doc);
            if (textEnd !== null) {
              event.preventDefault();
              view.dispatch(
                view.state.tr.setSelection(
                  TextSelection.create(view.state.doc, textEnd),
                ),
              );
              view.focus();
              return true;
            }

            return false;
          }

          const { rowPos, rowSize } = rowHit;

          const { selection } = view.state;

          const isCurrentlyNodeSelected =
            selection instanceof NodeSelection &&
            selection.node.type.name === "emailRow" &&
            selection.from === rowPos;

          // ── Double click: enter text editing mode ──
          if (isDoubleClick && isCurrentlyNodeSelected) {
            lastClickTime = 0;
            lastClickCoords = null;

            // Resolve position at click coordinates for TextSelection
            const coords = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            if (!coords) {
              // posAtCoords can fail on empty blocks (e.g. placeholder paragraph
              // with only a <br>). Fall back to the start of the row's content.
              event.preventDefault();
              const rowNode = view.state.doc.nodeAt(rowPos);
              if (rowNode) {
                const $rowStart = view.state.doc.resolve(rowPos + 1);
                const sel = TextSelection.near($rowStart, 1);
                view.dispatch(view.state.tr.setSelection(sel));
                view.focus();
              }
              return true;
            }

            // Don't enter text editing for non-text blocks (images)
            if (isInsideNonTextBlock(view, coords.pos)) {
              event.preventDefault();
              return true;
            }

            // Enter text editing with the clicked word selected
            event.preventDefault();
            const word = wordRangeAt(view, coords.pos);
            const tr = view.state.tr.setSelection(
              word
                ? TextSelection.create(view.state.doc, word.from, word.to)
                : TextSelection.near(view.state.doc.resolve(coords.pos)),
            );
            view.dispatch(tr);
            view.focus();
            return true;
          }

          // Update click tracking for double-click detection
          lastClickTime = now;
          lastClickCoords = { x: event.clientX, y: event.clientY };

          // ── Already NodeSelected on same row ──
          if (isCurrentlyNodeSelected) {
            const newCellIndex = findCellIndexFromEvent(view, event, rowPos);

            // Check if a different cell or block was clicked
            const clickCoords = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            const prevCellIndex = _clickedCellIndex;
            const prevType = _clickedNonTextBlockType;
            const prevBlockIndex = _clickedNonTextBlockIndex;

            _clickedCellIndex = newCellIndex;
            if (clickCoords) {
              updateBlockTracking(view, clickCoords.pos, rowPos, newCellIndex);
            }

            if (
              newCellIndex !== prevCellIndex ||
              _clickedNonTextBlockType !== prevType ||
              _clickedNonTextBlockIndex !== prevBlockIndex
            ) {
              // Clicked a different cell or a different block in the same cell —
              // re-dispatch NodeSelection to trigger bubble menu repositioning
              event.preventDefault();
              const tr = view.state.tr.setSelection(
                NodeSelection.create(view.state.doc, rowPos),
              );
              // Signal all BubbleMenu instances to recompute position
              tr.setMeta("bubbleMenu", "updatePosition");
              view.dispatch(tr);
              view.focus();
              return true;
            }

            // Same cell, same block re-click → drag behavior
            event.preventDefault();
            startDrag(view, event, rowPos, rowSize);
            return true;
          }

          // ── Currently in text editing mode within this row → stay
          // in text mode. Place cursor at click position and track mouse
          // movement for text highlighting (drag-to-select).
          if (selection instanceof TextSelection) {
            let isInSameRow = false;
            const $from = selection.$from;
            for (let d = $from.depth; d >= 0; d--) {
              if (
                $from.node(d).type.name === "emailRow" &&
                $from.before(d) === rowPos
              ) {
                isInSameRow = true;
                break;
              }
            }
            if (isInSameRow) {
              const coords = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              if (!coords) return false;

              // If clicking on a non-text block (image/button) within the
              // same row, break out of text editing and NodeSelect the row
              // so the appropriate bubble menu appears.
              _clickedCellIndex = findCellIndexFromEvent(view, event, rowPos);
              const clickedBlockType = updateBlockTracking(
                view,
                coords.pos,
                rowPos,
                _clickedCellIndex,
              );
              if (clickedBlockType) {
                event.preventDefault();
                const nsTr = view.state.tr.setSelection(
                  NodeSelection.create(view.state.doc, rowPos),
                );
                view.dispatch(nsTr);
                view.focus();
                return true;
              }

              // Double click in the row already being edited: select the word
              if (isDoubleClick) {
                const word = wordRangeAt(view, coords.pos);
                if (word) {
                  event.preventDefault();
                  view.dispatch(
                    view.state.tr.setSelection(
                      TextSelection.create(view.state.doc, word.from, word.to),
                    ),
                  );
                  view.focus();
                  return true;
                }
              }

              // Place cursor at click position
              const anchor = coords.pos;
              event.preventDefault();
              const clickTr = view.state.tr.setSelection(
                TextSelection.create(view.state.doc, anchor),
              );
              view.dispatch(clickTr);
              view.focus();

              // Track mousemove for drag-to-highlight
              const onMouseMove = (e: MouseEvent) => {
                const moveCoords = view.posAtCoords({
                  left: e.clientX,
                  top: e.clientY,
                });
                if (!moveCoords) return;
                const head = moveCoords.pos;
                try {
                  const dragTr = view.state.tr.setSelection(
                    TextSelection.create(view.state.doc, anchor, head),
                  );
                  view.dispatch(dragTr);
                } catch {
                  // positions may be out of bounds
                }
              };
              const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
              };
              document.addEventListener("mousemove", onMouseMove);
              document.addEventListener("mouseup", onMouseUp);

              return true;
            }
          }

          // ── Single click → NodeSelection on the row ──
          // Track which cell was clicked for bubble menu targeting
          _clickedCellIndex = findCellIndexFromEvent(view, event, rowPos);
          // Track which specific non-text block was clicked (if any)
          {
            const clickCoords = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            if (clickCoords) {
              updateBlockTracking(
                view,
                clickCoords.pos,
                rowPos,
                _clickedCellIndex,
              );
            } else {
              _clickedNonTextBlockType = null;
              _clickedNonTextBlockIndex = 0;
            }
          }
          event.preventDefault();
          const tr = view.state.tr.setSelection(
            NodeSelection.create(view.state.doc, rowPos),
          );
          view.dispatch(tr);
          view.focus();
          return true;
        },
      },
    },
  });
}
